import { useContext, useEffect } from "react";

import { CountryNames, OrganizationCountry } from "@simplyk/common";
import countryRegionData from "country-region-data";

import { mustDisplayAddressQuestion } from "../../../../helpers/form";
import { usePreviewContext } from "../../../LiveFormEditor/LivePreview/context/PreviewContext";
import { useFormV2Context } from "../../context/FormV2Context";

import { FormV2Step2SectionWrapper } from "./FormV2Step2SectionWrapper";
import { PublicDonationCheckbox, usePublicDonationCheckboxRenderPosition } from "./PublicDonationCheckBox";

import { FormGiveAsOrganism } from "@/components/PaymentInformation/FormGiveAsOrganism";
import { FormV2BuyerInformationSectionLayout } from "@/features/FormV2/Step2/sections/FormV2BuyerInformationSectionLayout";
import { FormSelectField, FormSelectItem } from "@/forms-design-system/FormSelectField";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";
import { isEmail, isNameValid, isRequired } from "@/helpers/validators";
import { useCountryRegion } from "@/hooks/useCountryRegion";
import { useDefaultCountryRegion } from "@/hooks/useDefaultCountryRegion";
import { useTranslate } from "@/hooks/useTranslate";
import { FrontendFormContext } from "@/src/contexts/FrontendFormContext";

export const FormV2BuyerInformationSection = () => {
  const { t } = useTranslate();
  const { organization, displayExpressCheckout, formData } = useContext(FrontendFormContext);

  const { form, type, displayGenerateReceipt } = useFormV2Context("common");

  const { setValue, watch, control } = form;
  const { isPreview } = usePreviewContext();

  const countryValue = watch("country");
  const hasGiftAidConfirmation = watch("hasGiftAidConfirmation");
  const { currentCountryRegions } = useCountryRegion(countryValue);
  const { defaultCountry, defaultRegion } = useDefaultCountryRegion(organization);
  const publicDonationCheckboxPosition = usePublicDonationCheckboxRenderPosition();

  const displayStateSelect = countryValue === CountryNames.Canada || countryValue === CountryNames.UnitedStates;

  useEffect(() => {
    setValue("country", defaultCountry);
  }, [defaultCountry, setValue]);

  useEffect(() => {
    // reset region when country changes
    if (countryValue === defaultCountry) {
      setValue("region", defaultRegion);
    } else {
      setValue("region", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryValue]);

  const shouldDisplayAddressQuestion = mustDisplayAddressQuestion({
    displayGenerateReceipt,
    hasGiftAidConfirmation,
    organization,
    formData,
  });

  return (
    <FormV2Step2SectionWrapper title={t("ticketing", "yourInformation")}>
      <FormV2BuyerInformationSectionLayout
        slots={{
          email: (
            <FormTextField
              control={control}
              name="email"
              rules={[
                isRequired(t("ticketing", "emailRequired")),
                isEmail(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
              ]}
              type="email"
              label={t("ticketing", "email")}
              data-test="form-email"
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.email?.message)}
              helperText={form.formState.errors.email?.message}
              required
            />
          ),
          firstName: (
            <FormTextField
              control={control}
              name="firstName"
              rules={[
                isRequired(t("ticketing", "firstNameRequired")),
                isNameValid(t("common", "invalidCharacterUsed")),
              ]}
              label={t("ticketing", "firstName")}
              data-test="form-firstname"
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.firstName?.message)}
              helperText={form.formState.errors.firstName?.message}
              required
            />
          ),
          lastName: (
            <FormTextField
              control={control}
              name="lastName"
              rules={[isRequired(t("ticketing", "lastNameRequired")), isNameValid(t("common", "invalidCharacterUsed"))]}
              label={t("ticketing", "lastName")}
              data-test="form-lastname"
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.lastName?.message)}
              helperText={form.formState.errors.lastName?.message}
              required
            />
          ),
          publicDonation: publicDonationCheckboxPosition === "your-information-section" && <PublicDonationCheckbox />,
          address: shouldDisplayAddressQuestion && (
            <FormTextField
              control={control}
              name="address"
              rules={[isRequired(t("ticketing", "addressRequired"))]}
              label={t("ticketing", "address")}
              data-test="form-address"
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.address?.message)}
              helperText={form.formState.errors.address?.message}
              required
            />
          ),
          city: shouldDisplayAddressQuestion && (
            <FormTextField
              control={control}
              name="city"
              rules={[isRequired(t("ticketing", "cityRequired"))]}
              label={t("ticketing", "city")}
              data-test="form-city"
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.city?.message)}
              helperText={form.formState.errors.city?.message}
              required
            />
          ),
          postalCode: shouldDisplayAddressQuestion && (
            <FormTextField
              control={control}
              name="postalCode"
              rules={[
                isRequired(
                  t(
                    "ticketing",
                    organization.country === OrganizationCountry.Canada ? "postalCodeRequired" : "zipCodeRequired"
                  )
                ),
              ]}
              label={
                organization.country === OrganizationCountry.Canada
                  ? t("ticketing", "postalCode")
                  : t("ticketing", "zipCode")
              }
              data-test="form-postalcode"
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.postalCode?.message)}
              helperText={form.formState.errors.postalCode?.message}
              required
            />
          ),
          country: (
            <FormSelectField
              control={control}
              name="country"
              rules={[isRequired(t("common", "fieldIsRequired"))]}
              label={t("ticketing", "country")}
              data-test="form-country"
              defaultValue={defaultCountry}
              disableInjectingGlobalStyles={isPreview}
              required
            >
              {countryRegionData.map((country) => (
                <FormSelectItem key={country.countryName} value={country.countryName} data-test="form-country-option">
                  {`${country.countryName} (${country.countryShortCode})`}
                </FormSelectItem>
              ))}
            </FormSelectField>
          ),
          region: displayStateSelect && (
            <FormSelectField
              control={control}
              name="region"
              rules={[isRequired(displayStateSelect, t("common", "fieldIsRequired"))]}
              label={countryValue === "Canada" ? t("ticketing", "region") : t("ticketing", "state")}
              data-test="form-region"
              disableInjectingGlobalStyles={isPreview}
              required
              error={Boolean(form.formState.errors.region?.message)}
              defaultValue={defaultRegion}
            >
              {currentCountryRegions.map((region, index) => (
                <FormSelectItem key={index} value={region.name} data-test="form-region-option">
                  {`${region.name} (${region.shortCode})`}
                </FormSelectItem>
              ))}
            </FormSelectField>
          ),
          organizationDonation: !displayExpressCheckout && (
            <FormGiveAsOrganism form={form} amplitudePayload={{}} formType={type} />
          ),
        }}
      />
    </FormV2Step2SectionWrapper>
  );
};
